import { Component, DestroyRef, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ActivatedRoute, Router } from '@angular/router';
import { provideTranslocoScope, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { HeaderService } from 'core';
import { ManualCustomer, isManualCustomer } from '../../interfaces/company-search-result.model';
import { CustomerConfigurationService } from '../../services/customer-configuration.service';

@Component({
  selector: 'ord-create-customer',
  imports: [TranslocoModule, ReactiveFormsModule, MatButtonModule, MatCheckboxModule, MatFormFieldModule, MatInputModule],
  providers: [provideTranslocoScope('orders')],
  templateUrl: './create-customer.html',
  styleUrl: './create-customer.scss',
})
export class CreateCustomer implements OnInit, OnDestroy {
  private readonly headerService = inject(HeaderService);
  private readonly transloco = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly configuration = inject(CustomerConfigurationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  readonly proposalId = this.route.snapshot.queryParamMap.get('id');
  // TODO: Aggiungere la verifica completa di correttezza di codice fiscale e partita IVA.
  readonly form = inject(FormBuilder).nonNullable.group({
    ragioneSociale: ['', [Validators.required, Validators.pattern(/\S/)]],
    codiceFiscale: ['', [Validators.required, Validators.pattern(/^(?:[a-zA-Z0-9]{16}|[0-9]{11})$/)]],
    partitaIva: ['', [Validators.required, Validators.pattern(/^[0-9]{11}$/)]],
    indirizzo: ['', [Validators.required, Validators.pattern(/\S/)]],
    localita: ['', [Validators.required, Validators.pattern(/\S/)]],
    provincia: ['', [Validators.required, Validators.pattern(/^[a-zA-Z]{2}$/)]],
    cap: ['', [Validators.required, Validators.pattern(/^[0-9]{5}$/)]],
    useAddressAsDestination: [false],
  });

  ngOnInit(): void {
    this.headerService.removeActions();
    this.transloco.selectTranslate<string>('newCustomer.title', {}, 'orders')
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(title => this.headerService.setTitle(title));
    const customer = this.configuration.customer();
    if (customer && isManualCustomer(customer) && this.configuration.proposalId() === this.proposalId) {
      this.form.setValue({
        ragioneSociale: customer.ragioneSociale,
        codiceFiscale: customer.codiceFiscale,
        partitaIva: customer.partitaIva,
        indirizzo: customer.indirizzo,
        localita: customer.localita,
        provincia: customer.provincia,
        cap: customer.cap,
        useAddressAsDestination: this.configuration.useManualAddressAsDestination(),
      });
    }
  }

  ngOnDestroy(): void {
    this.headerService.removeTitle();
    this.headerService.removeActions();
  }

  continue(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) {
      return;
    }
    const value = this.form.getRawValue();
    const previous = this.configuration.customer();
    const previousManual = previous && isManualCustomer(previous) && this.configuration.proposalId() === this.proposalId ? previous : null;
    const customer: ManualCustomer = {
      source: 'manual',
      draftId: previousManual?.draftId ?? crypto.randomUUID(),
      idBusinessPartner: null,
      cribis: false,
      ragioneSociale: value.ragioneSociale.trim(),
      codiceFiscale: value.codiceFiscale.toUpperCase(),
      partitaIva: value.partitaIva,
      indirizzo: value.indirizzo.trim(),
      localita: value.localita.trim(),
      provincia: value.provincia.toUpperCase(),
      cap: value.cap,
      destini: [],
    };
    if (previousManual) {
      this.configuration.customer.set(customer);
    } else {
      this.configuration.begin(customer, this.proposalId);
    }
    this.configuration.useManualAddressAsDestination.set(value.useAddressAsDestination);
    const destination = this.configuration.newDestination();
    if (value.useAddressAsDestination && destination) {
      this.configuration.setNewDestination({
        ...destination,
        citta: customer.localita,
        provincia: customer.provincia,
        cap: customer.cap,
        indirizzo: customer.indirizzo,
      });
    }
    // TODO: Inviare l'anagrafica manuale alla futura API al completamento del flusso.
    void this.router.navigate(['/new-order-proposal/configure-customer'], {
      queryParams: { id: this.proposalId },
      state: this.configuration.getFlowState(),
    });
  }
}
